import React, { useState } from 'react';
import Tooltip from '../ui/Tooltip';

function SmartCropDialog({ onClose, onConfirm, smartFrame, onSmartFrameChange, frameAnchor, onFrameAnchorChange, drawFrame, onDrawFrameChange, onDrawFrameCommit, onCancel, imageDimensions }) {
  const [dimensions, setDimensions] = useState([
    { width: '', height: '' },
    { width: '', height: '' },
  ]);
  const [error, setError] = useState('');
  const anchors = [
    ['Top left', 0, 0], ['Top center', 0.5, 0], ['Top right', 1, 0],
    ['Middle left', 0, 0.5], ['Center', 0.5, 0.5], ['Middle right', 1, 0.5],
    ['Bottom left', 0, 1], ['Bottom center', 0.5, 1], ['Bottom right', 1, 1],
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (drawFrame) {
      onDrawFrameCommit();
      return;
    }
    const dims = dimensions
      .map(({ width, height, constrain }) => ({ width: Number(width), height: Number(height), constrain }))
      .filter(({ width, height }) => width > 0 && height > 0);
    if (!dims.length) {
      setError('Enter a width and height for at least one frame.');
      return;
    }
    onConfirm(dims);
  };

  const updateDimension = (index, field, value) => {
    setDimensions((current) => {
      const updated = current.map((dimension, dimensionIndex) => (
      dimensionIndex === index ? { ...dimension, [field]: value } : dimension
      ));
      const activeDimensions = updated.filter(({ width, height }) => width || height);
      return [...activeDimensions, { width: '', height: '' }];
    });
  };

  const removeDimension = (indexToRemove) => {
    setDimensions((current) => {
      const updated = current.filter((_, i) => i !== indexToRemove);
      const activeDimensions = updated.filter(({ width, height }) => width || height);
      return [...activeDimensions, { width: '', height: '' }];
    });
  };

  return (
    <div className="smart-crop-dialog">
      <form
        onSubmit={handleSubmit}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
          }
        }}
      >
        <div className="dimension-dialog-controls">
          <div className={`mode-control${smartFrame ? ' active' : ''}`} onClick={() => {
            onDrawFrameChange(false);
            onSmartFrameChange(true);
          }}>
            <Tooltip message="Smart Frames"><button
              type="button"
              className={`smart-crop-toggle${smartFrame ? ' active' : ''}`}
              onClick={() => {
                onDrawFrameChange(false);
                onSmartFrameChange(true);
              }}
              aria-label="Toggle smart frames"
              aria-pressed={smartFrame}
              tabIndex={-1}
            >
              <span className="smart-frame-icon" aria-hidden="true">
                <span className="smart-frame-icon-horizontal" />
                <span className="smart-frame-icon-vertical" />
                <span className="smart-frame-icon-mark">*</span>
              </span>
            </button></Tooltip>
          </div>
          <div className={`mode-control${!smartFrame && !drawFrame ? ' active' : ''}`} onClick={() => {
            onDrawFrameChange(false);
            onSmartFrameChange(false);
            onFrameAnchorChange(frameAnchor);
          }}>
            <div
              className={`frame-anchor-grid${!smartFrame && !drawFrame ? ' active' : ''}`}
              aria-label="Frame anchor"
              onClick={(event) => {
                if (event.target === event.currentTarget) {
                  onDrawFrameChange(false);
                  onFrameAnchorChange(frameAnchor);
                }
              }}
            >
              {anchors.map(([label, x, y]) => (
                <Tooltip key={label} message={label}><button
                  type="button"
                  className={`frame-anchor-button${frameAnchor.x === x && frameAnchor.y === y ? ' active' : ''}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDrawFrameChange(false);
                    onFrameAnchorChange({ x, y });
                  }}
                  aria-label={label}
                  aria-pressed={frameAnchor.x === x && frameAnchor.y === y}
                  tabIndex={-1}
                />
                </Tooltip>
              ))}
            </div>
          </div>
          <div className={`mode-control${drawFrame ? ' active' : ''}`} onClick={() => {
            onSmartFrameChange(false);
            onDrawFrameChange(true);
          }}>
            <button
              type="button"
              className={`draw-frame-toggle${drawFrame ? ' active' : ''}`}
              onClick={(event) => {
                event.stopPropagation();
                onSmartFrameChange(false);
                onDrawFrameChange(!drawFrame);
              }}
              aria-label="Toggle draw frames"
              aria-pressed={drawFrame}
              title="Draw Frames"
              tabIndex={-1}
            >
              <span aria-hidden="true">&#9998;</span>
            </button>
          </div>
        </div>
        <header className="smart-crop-dialog-header">
          {drawFrame ? 'Free Draw' : smartFrame ? 'Smart Placement' : 'Anchor Placement'}
        </header>
        {!drawFrame && <div className="smart-crop-dimensions">
          {dimensions.map((dimension, index) => (
            <div
              className={`smart-crop-dimension-group${index === dimensions.length - 1 ? ' ghost' : ''}${Number(dimension.width) > imageDimensions.width || Number(dimension.height) > imageDimensions.height ? ' oversize' : ''}`}
              key={index}
            >
              <label className="toolbar-dialog-field">
                <span className="toolbar-dialog-field-prefix">W</span>
                <input
                  type="number"
                  min="1"
                  value={dimension.width}
                  onChange={(event) => updateDimension(index, 'width', event.target.value)}
                  autoFocus={index === 0}
                />
              </label>
              <label className="toolbar-dialog-field">
                <span className="toolbar-dialog-field-prefix">H</span>
                <input
                  type="number"
                  min="1"
                  value={dimension.height}
                  onChange={(event) => updateDimension(index, 'height', event.target.value)}
                />
              </label>
              {(Number(dimension.width) > imageDimensions.width || Number(dimension.height) > imageDimensions.height) && (
                <Tooltip message="Constrain crop area to image bounds.">
                  <label className="constrain-dimension-toggle">
                    <input
                      type="checkbox"
                      checked={Boolean(dimension.constrain)}
                      onChange={(event) => updateDimension(index, 'constrain', event.target.checked)}
                    />
                    <span className="constrain-icon" aria-hidden="true">
                      <span>&#8600;</span><span>&#8601;</span><span>&#8599;</span><span>&#8598;</span>
                    </span>
                    <span className="sr-only">Constrain to image bounds</span>
                  </label>
                </Tooltip>
              )}
              {!(Number(dimension.width) > imageDimensions.width || Number(dimension.height) > imageDimensions.height) && (
                <div className="constrain-dimension-toggle spacer" aria-hidden="true" />
              )}
              {index !== dimensions.length - 1 ? (
                <button
                  type="button"
                  className="smart-crop-dim-remove"
                  onClick={() => removeDimension(index)}
                  aria-label="Remove dimension"
                  tabIndex={-1}
                >
                  -
                </button>
              ) : (
                <div className="smart-crop-dim-remove spacer" aria-hidden="true" />
              )}
            </div>
          ))}
        </div>}
        {error && <div className="smart-crop-error">{error}</div>}
        <div className="smart-crop-actions">
          <button type="submit" className="smart-crop-btn smart-crop-submit" tabIndex={-1}>{drawFrame ? 'Commit' : smartFrame ? 'Add' : 'Add'}</button>
          <button type="button" onClick={onCancel} className="smart-crop-btn smart-crop-cancel" tabIndex={-1}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default SmartCropDialog;
